import React, { useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { css } from '@emotion/css'
import Swal from 'sweetalert2'
import { BaseComponentWithContextProps, componentWithContext } from '../../../../base/customHooks/componentHOC'
import { useCustomReducer } from '../../../../base/customHooks'
import IPButton from '../../../../components/IPButton'
import IPModal from '../../../../components/IPModal'
import IPInput from '../../../../components/IPInput'
import IPSelectBox from '../../../../components/IPSelectBox'
import { ISelectOption } from '../../../../components/IPSelectBox/IPSelectBox'
import Table from '../../../../components/Table'
import {
  installmentOptions,
  IPkHierarchy,
  IPkSetting,
  IPkSettingHistory,
  PK_POS_AKODE,
  PK_POS_NORMAL,
  PkDeadlineUnit,
  pkPosTypeLabel,
} from '../models/models'
import { GetPkSettingsApi } from '../services/getPkSettingsApi'
import { UpdatePkSettingsApi } from '../services/updatePkSettingsApi'
import { ListPkHierarchyApi } from '../services/listPkHierarchyApi'
import { SavePkHierarchyApi } from '../services/savePkHierarchyApi'
import { DeletePkHierarchyApi } from '../services/deletePkHierarchyApi'
import { ListPkSettingHistoryApi } from '../services/listPkSettingHistoryApi'

interface IPkSettingsState {
  settings: IPkSetting
  hierarchy: IPkHierarchy[]
  history: IPkSettingHistory[]
  // Hiyerarşi ekle / düzenle modalı
  showHierarchy: boolean
  isEditHierarchy: boolean
  hierarchyCode: string
  hierarchyDescription: string
  hierarchyInstallments: ISelectOption[]
}

const HIERARCHY_CODE_MAX = 18
const HIERARCHY_DESCRIPTION_MAX = 500
const invoiceInstallmentOptions = installmentOptions(1, 12)
const hierarchyInstallmentOptions = installmentOptions(2, 12)
const unitOptions: ISelectOption[] = [
  { value: 1, label: 'İş günü' },
  { value: 2, label: 'Takvim günü' },
]
// Taksit başına POS türü: 1 = Normal POS, 3 = AkÖde, 0 = Kapalı (atanmamış, taksit sunulmaz)
const POS_CLOSED = 0
const posMapInstallments = Array.from({ length: 12 }, (_, i) => i + 1)
const posMapChoices = [
  { value: PK_POS_NORMAL, label: pkPosTypeLabel(PK_POS_NORMAL) },
  { value: PK_POS_AKODE, label: pkPosTypeLabel(PK_POS_AKODE) },
  { value: POS_CLOSED, label: 'Kapalı' },
]
const iconButton = css`
  cursor: pointer;
  margin-right: 8px;
`
const toOptions = (numbers: number[]) => (numbers || []).map(n => ({ value: n, label: `${n} Taksit` }))
const toNumbers = (options: ISelectOption[]) =>
  (options || [])
    .map(o => Number(o.value))
    .sort((a, b) => a - b)

// Ayar geçmişi değerleri ayarın JSON'udur (hiyerarşi geçmişinde taksit listesi); okunur satırlara çevrilir.
const historyLabels: Record<string, string> = {
  PkPaymentEnabled: 'PK ödemesi açık',
  InstallmentsEnabled: 'Taksit açık',
  PkUseAkOde: "AkÖde'den çek (eski)",
  NormalPosInstallments: 'Normal POS taksitleri',
  AkOdeInstallments: 'AkÖde taksitleri',
  InvoiceControlEnabled: 'Fatura kontrolü açık',
  InvoiceDeadlineUnit: 'Fatura süresi birimi',
  InvoiceDeadlineValue: 'Fatura süresi',
  InvoiceRequiredInstallments: 'Fatura istenen taksitler',
}

const parseHistoryValue = (value?: string | null): Record<string, any> | null => {
  if (!value) return null
  try {
    const parsed = JSON.parse(value)
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null
  } catch {
    return null
  }
}

const formatHistoryField = (key: string, value: any) => {
  if (typeof value === 'boolean') return value ? 'Evet' : 'Hayır'
  if (Array.isArray(value)) return value.length > 0 ? value.join(', ') : '-'
  if (key === 'InvoiceDeadlineUnit') return Number(value) === 2 ? 'Takvim günü' : 'İş günü'
  if (value === null || value === undefined || value === '') return '-'
  return String(value)
}

// Diğer taraftan farklı olan alanlar kalın gösterilir.
const HistoryValue = ({ value, other }: { value?: string | null; other?: string | null }) => {
  const parsed = parseHistoryValue(value)
  if (!parsed) return <span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{value || '-'}</span>
  const otherParsed = parseHistoryValue(other)
  return (
    <ul className='list-unstyled mb-0' style={{ minWidth: 240 }}>
      {Object.keys(parsed).map(key => {
        const text = formatHistoryField(key, parsed[key])
        const changed = otherParsed !== null && formatHistoryField(key, otherParsed[key]) !== text
        return (
          <li key={key} style={{ fontWeight: changed ? 600 : undefined, color: changed ? '#b02a37' : undefined }}>
            {historyLabels[key] || key}: {text}
          </li>
        )
      })}
    </ul>
  )
}

function PkSettings(props: BaseComponentWithContextProps) {
  const [state, setState] = useCustomReducer<IPkSettingsState>({
    settings: {
      PkPaymentEnabled: true,
      InstallmentsEnabled: false,
      NormalPosInstallments: [],
      AkOdeInstallments: [],
      InvoiceControlEnabled: false,
      InvoiceDeadlineUnit: 1,
      InvoiceDeadlineValue: 3,
      InvoiceRequiredInstallments: [],
    },
    hierarchy: [],
    history: [],
    showHierarchy: false,
    isEditHierarchy: false,
    hierarchyCode: '',
    hierarchyDescription: '',
    hierarchyInstallments: [],
  })

  const showError = (err: any) =>
    Swal.fire({ icon: 'error', title: err?.description, showConfirmButton: true, allowOutsideClick: false })
  const showWarning = (title: string) =>
    Swal.fire({ icon: 'error', title, showConfirmButton: true, allowOutsideClick: false })
  const showSuccess = () =>
    Swal.fire({ icon: 'success', title: 'İşlem başarılı', showConfirmButton: true, allowOutsideClick: false })

  useEffect(() => {
    loadAll()
  }, [])

  const loadAll = async () => {
    props.showLoading()
    try {
      const [settings, hierarchy, history] = await Promise.all([
        new GetPkSettingsApi({}).getSettings(),
        new ListPkHierarchyApi({}).listHierarchy(),
        new ListPkSettingHistoryApi({}).listSettingHistory(),
      ])
      setState({ settings: normalizeSettings(settings) || state.settings, hierarchy: hierarchy || [], history: history || [] })
    } catch (err: any) {
      showError(err)
    } finally {
      props.hideLoading()
    }
  }

  const setSetting = (patch: Partial<IPkSetting>) => setState({ settings: { ...state.settings, ...patch } })

  const normalizeSettings = (settings?: IPkSetting | null): IPkSetting | undefined =>
    settings
      ? { ...settings, NormalPosInstallments: settings.NormalPosInstallments || [], AkOdeInstallments: settings.AkOdeInstallments || [] }
      : undefined

  // Bir taksit yalnız tek listede bulunur; kesişme yapı gereği oluşmaz.
  const installmentPosType = (installment: number): number =>
    state.settings.NormalPosInstallments.includes(installment)
      ? PK_POS_NORMAL
      : state.settings.AkOdeInstallments.includes(installment)
      ? PK_POS_AKODE
      : POS_CLOSED

  const setInstallmentPosType = (installment: number, posType: number) => {
    const without = (list: number[]) => list.filter(n => n !== installment)
    const withIt = (list: number[]) => [...without(list), installment].sort((a, b) => a - b)
    setSetting({
      NormalPosInstallments: posType === PK_POS_NORMAL ? withIt(state.settings.NormalPosInstallments) : without(state.settings.NormalPosInstallments),
      AkOdeInstallments: posType === PK_POS_AKODE ? withIt(state.settings.AkOdeInstallments) : without(state.settings.AkOdeInstallments),
    })
  }

  // ---- Genel ayarlar + fatura kontrolü (tek kayıt) ----
  const saveSettings = async () => {
    const days = Number(state.settings.InvoiceDeadlineValue)
    if (!Number.isInteger(days) || days < 1 || days > 30) return showWarning('Fatura süresi 1-30 arasında bir gün sayısı olmalıdır.')

    props.showLoading()
    try {
      const saved = await new UpdatePkSettingsApi({}).updateSettings({ ...state.settings, InvoiceDeadlineValue: days })
      const history = await new ListPkSettingHistoryApi({}).listSettingHistory()
      setState({ settings: normalizeSettings(saved) || state.settings, history: history || [] })
      showSuccess()
    } catch (err: any) {
      showError(err)
    } finally {
      props.hideLoading()
    }
  }

  // ---- Hiyerarşi ----
  const openAddHierarchy = () =>
    setState({ showHierarchy: true, isEditHierarchy: false, hierarchyCode: '', hierarchyDescription: '', hierarchyInstallments: [] })

  const openEditHierarchy = (h: IPkHierarchy) =>
    setState({
      showHierarchy: true,
      isEditHierarchy: true,
      hierarchyCode: h.HierarchyCode,
      hierarchyDescription: h.Description || '',
      hierarchyInstallments: toOptions(h.Installments),
    })

  const saveHierarchy = async () => {
    const code = state.hierarchyCode.trim()
    if (!code) return showWarning('Hiyerarşi kodu zorunludur.')
    if (state.hierarchyInstallments.length === 0) return showWarning('En az bir taksit seçilmelidir.')

    props.showLoading()
    try {
      await new SavePkHierarchyApi({}).saveHierarchy({
        HierarchyCode: code,
        Description: state.hierarchyDescription.trim(),
        Installments: toNumbers(state.hierarchyInstallments),
      })
      const [hierarchy, history] = await Promise.all([new ListPkHierarchyApi({}).listHierarchy(), new ListPkSettingHistoryApi({}).listSettingHistory()])
      setState({ showHierarchy: false, hierarchy: hierarchy || [], history: history || [] })
      showSuccess()
    } catch (err: any) {
      showError(err)
    } finally {
      props.hideLoading()
    }
  }

  const deleteHierarchy = (code: string) => {
    Swal.fire({
      icon: 'question',
      title: 'Silmek istediğinize emin misiniz?',
      showConfirmButton: true,
      allowOutsideClick: false,
      showDenyButton: true,
      confirmButtonText: 'Evet',
      denyButtonText: 'Hayır',
    }).then(async result => {
      if (!result.isConfirmed) return
      props.showLoading()
      try {
        await new DeletePkHierarchyApi({}).deleteHierarchy(code)
        const [hierarchy, history] = await Promise.all([new ListPkHierarchyApi({}).listHierarchy(), new ListPkSettingHistoryApi({}).listSettingHistory()])
        setState({ hierarchy: hierarchy || [], history: history || [] })
        showSuccess()
      } catch (err: any) {
        showError(err)
      } finally {
        props.hideLoading()
      }
    })
  }

  const hierarchyColumns = useMemo(
    () => [
      { Header: 'Hiyerarşi Kodu', accessor: 'col1' },
      { Header: 'Açıklama', accessor: 'col2' },
      { Header: 'İzinli Taksitler', accessor: 'col3' },
      { Header: 'İşlemler', accessor: 'col4' },
    ],
    []
  )

  const hierarchyData = useMemo(
    () =>
      state.hierarchy.map(h => ({
        col1: h.HierarchyCode,
        col2: h.Description,
        col3: [...h.Installments].sort((a, b) => a - b).join(', '),
        col4: (
          <>
            <a role='button' className={iconButton} title='Düzenle' onClick={() => openEditHierarchy(h)}>
              <i className={`${css`font-size: 1.2rem; color: green;`} icon-edit`}></i>
            </a>
            <a role='button' className={iconButton} title='Sil' onClick={() => deleteHierarchy(h.HierarchyCode)}>
              <i className={`${css`font-size: 1.2rem; color: red;`} icon-trash-2`}></i>
            </a>
          </>
        ),
      })),
    [state.hierarchy]
  )

  const s = state.settings

  return (
    <div className=''>
      <div className='content col-lg-12 m-auto'>
        <div className='card'>
          <div className='card-header d-flex justify-content-between align-items-center flex-wrap'>
            <div className='col-12 col-md-8 mb-2'>
              <span className='h4'>Peşine Kontrat Ayarları</span>
            </div>
            <div className='col-12 col-md-4 mb-2 d-flex justify-content-end'>
              <Link to='/admin/PesineKontrat/Rates'>
                <IPButton text='PK Oran Tanımları' type='button' className='btn btn-primary' style={{ width: 'auto', height: '45px', padding: '0 20px' }} />
              </Link>
            </div>
          </div>
          <div className='card-body'>
            <h5>Genel</h5>
            <div className='form-row'>
              <div className='form-group col-md-12'>
                <label className='labeltext'>
                  <input type='checkbox' checked={s.PkPaymentEnabled} onChange={e => setSetting({ PkPaymentEnabled: e.target.checked })} /> PK ödemesi açık
                </label>
              </div>
              <div className='form-group col-md-12'>
                <label className='labeltext'>
                  <input type='checkbox' checked={s.InstallmentsEnabled} onChange={e => setSetting({ InstallmentsEnabled: e.target.checked })} /> Taksit seçenekleri açık
                </label>
              </div>
            </div>

            <hr />
            <h5>Taksit Bazlı POS Türü</h5>
            <p className='text-muted mb-2'>
              Her taksit seçilen POS türünden çekilir ve o türün oranıyla sunulur. Normal POS, kartın bankasının kendi sanal POS'udur. Kapalı
              taksit ve seçilen türde oranı tanımlı olmayan taksit sunulmaz.
            </p>
            <div style={{ overflowX: 'auto' }} className='mb-2'>
              <table className='table table-bordered table-sm mb-0' role='grid' style={{ width: 'auto' }}>
                <thead className='tableodd'>
                  <tr role='row'>
                    <th style={{ whiteSpace: 'nowrap' }}>Taksit</th>
                    {posMapInstallments.map(i => (
                      <th key={i} style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                        {i === 1 ? 'Tek Çekim' : i}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr role='row'>
                    <td style={{ whiteSpace: 'nowrap' }}>POS Türü</td>
                    {posMapInstallments.map(i => (
                      <td key={i} style={{ minWidth: '115px' }}>
                        <select
                          className='form-control form-control-sm'
                          value={installmentPosType(i)}
                          onChange={e => setInstallmentPosType(i, Number(e.target.value))}
                        >
                          {posMapChoices.map(c => (
                            <option key={c.value} value={c.value}>
                              {c.label}
                            </option>
                          ))}
                        </select>
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
            <small className='form-text text-muted mb-3'>
              Normal POS: {s.NormalPosInstallments.length > 0 ? s.NormalPosInstallments.join(', ') : '-'} · AkÖde:{' '}
              {s.AkOdeInstallments.length > 0 ? s.AkOdeInstallments.join(', ') : '-'}
            </small>

            <hr />
            <h5>Fatura Kontrolü</h5>
            <div className='form-row'>
              <div className='form-group col-md-12'>
                <label className='labeltext'>
                  <input type='checkbox' checked={s.InvoiceControlEnabled} onChange={e => setSetting({ InvoiceControlEnabled: e.target.checked })} /> Fatura kontrolü açık
                </label>
              </div>
              <div className='form-group col-md-3'>
                <label className='labeltext'>Süre Birimi</label>
                <IPSelectBox
                  isClearable={false}
                  options={unitOptions}
                  value={unitOptions.find(o => o.value === s.InvoiceDeadlineUnit)}
                  onChangeSingle={option => setSetting({ InvoiceDeadlineUnit: Number(option.value) as PkDeadlineUnit })}
                />
              </div>
              <div className='form-group col-md-2'>
                <label className='labeltext'>Gün Sayısı (1-30)</label>
                <IPInput
                  type='number'
                  min={1}
                  max={30}
                  value={s.InvoiceDeadlineValue}
                  onChange={e => setSetting({ InvoiceDeadlineValue: e.target.value === '' ? 0 : Number(e.target.value) })}
                />
              </div>
              <div className='form-group col-md-7'>
                <label className='labeltext'>Faturanın İstendiği Taksitler</label>
                <IPSelectBox
                  isMulti
                  isClearable
                  closeMenuOnSelect={false}
                  placeholder='Taksit seçiniz'
                  options={invoiceInstallmentOptions}
                  value={toOptions(s.InvoiceRequiredInstallments)}
                  onChangeMulti={options => setSetting({ InvoiceRequiredInstallments: toNumbers(options) })}
                />
              </div>
            </div>
            <div className='form-row mt-2 mb-4'>
              <div className='col-md-12 d-flex justify-content-end'>
                <IPButton text='Ayarları Kaydet' type='button' className='btn btn-success' style={{ width: 'auto', height: '45px', padding: '0 20px' }} onClick={saveSettings} />
              </div>
            </div>

            <hr />
            <div className='d-flex justify-content-between align-items-center'>
              <h5>Hiyerarşi Bazlı Taksitler</h5>
              <IPButton text='Hiyerarşi Ekle' type='button' className='btn btn-primary' style={{ width: 'auto', height: '45px', padding: '0 20px' }} onClick={openAddHierarchy} />
            </div>
            <p className='text-muted mt-2'>En uzun eşleşen hiyerarşi kodu geçerlidir; eşleşme yoksa yalnız tek çekim sunulur.</p>
            <div style={{ overflowX: 'auto' }}>
              <Table columns={hierarchyColumns} data={hierarchyData} currentPage={0} />
            </div>

            <hr />
            <h5>Ayar Geçmişi</h5>
            <div style={{ maxHeight: 600, overflow: 'auto', width: '100%' }}>
              <table className='table table-bordered table-hover dataTable mb-0' role='grid' style={{ minWidth: 1000 }}>
                <thead className='tableodd'>
                  <tr role='row'>
                    <th>#</th>
                    <th>Tarih</th>
                    <th>Kaynak</th>
                    <th>Hiyerarşi Kodu</th>
                    <th>İşlem</th>
                    <th>Eski Değer</th>
                    <th>Yeni Değer</th>
                    <th>Kullanıcı</th>
                  </tr>
                </thead>
                <tbody>
                  {state.history.map((h, index) => (
                    <tr role='row' className='odd' key={`${h.CreatedDate}-${index}`}>
                      <td>{index + 1}</td>
                      <td>{new Date(h.CreatedDate).toLocaleString('tr-TR')}</td>
                      <td>{h.Source}</td>
                      <td>{h.HierarchyCode}</td>
                      <td>{h.ProcessInformation}</td>
                      <td style={{ verticalAlign: 'top' }}><HistoryValue value={h.OldValue} other={h.NewValue} /></td>
                      <td style={{ verticalAlign: 'top' }}><HistoryValue value={h.NewValue} other={h.OldValue} /></td>
                      <td>{h.UserName}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <IPModal
        show={state.showHierarchy}
        onClose={() => setState({ showHierarchy: false })}
        onConfirm={saveHierarchy}
        confirmText='Kaydet'
        width={700}
        title={state.isEditHierarchy ? 'Hiyerarşi Düzenle' : 'Hiyerarşi Ekle'}
      >
        <div>
          <div className='form-row'>
            <div className='form-group col-md-6'>
              <label className='labeltext'>Hiyerarşi Kodu</label>
              <IPInput
                type='text'
                maxLength={HIERARCHY_CODE_MAX}
                disabled={state.isEditHierarchy}
                value={state.hierarchyCode}
                onChange={e => setState({ hierarchyCode: e.target.value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase() })}
              />
              <small className='form-text text-muted'>En fazla {HIERARCHY_CODE_MAX} karakter; yalnız harf ve rakam.</small>
            </div>
            <div className='form-group col-md-6'>
              <label className='labeltext'>Açıklama</label>
              <IPInput type='text' maxLength={HIERARCHY_DESCRIPTION_MAX} value={state.hierarchyDescription} onChange={e => setState({ hierarchyDescription: e.target.value })} />
            </div>
          </div>
          <div className='form-row'>
            <div className='form-group col-md-12'>
              <label className='labeltext'>İzinli Taksitler (2-12)</label>
              <IPSelectBox
                isMulti
                isClearable
                closeMenuOnSelect={false}
                menuPortalTarget={document.body}
                placeholder='Taksit seçiniz'
                options={hierarchyInstallmentOptions}
                value={state.hierarchyInstallments}
                onChangeMulti={options => setState({ hierarchyInstallments: options || [] })}
              />
            </div>
          </div>
        </div>
      </IPModal>
    </div>
  )
}

export default componentWithContext(PkSettings)
